/**
 * 装配体配合命令。
 *
 * 文件职责：
 * 1. 定义 Add/Edit/Delete/Suppress Mate 的领域命令；
 * 2. 将命令执行收口到 Mate 文档纯函数；
 * 3. 为后续接入模型 Undo/Redo 提供稳定命令边界。
 */

import {
  addAssemblyMateDefinition,
  deleteAssemblyMateDefinition,
  editAssemblyMateDefinition,
  lockAssemblyConcentricRotationMates,
  suppressAssemblyMateDefinition,
  type AssemblyMateDocumentModel,
  type LockAssemblyConcentricRotationMatesScope,
} from '../document';
import type { AssemblyMateDefinition } from '../definition';

/**
 * 新增装配体配合命令。
 */
export interface AddAssemblyMateCommand {
  /** 命令类型。 */
  readonly type: 'assemblyMate.add';
  /** 要新增的 Mate Definition。 */
  readonly mateDefinition: AssemblyMateDefinition;
}

/**
 * 编辑装配体配合命令。
 */
export interface EditAssemblyMateCommand {
  /** 命令类型。 */
  readonly type: 'assemblyMate.edit';
  /** 要编辑的 Mate ID。 */
  readonly mateId: string;
  /** Mate Definition 更新函数。 */
  readonly updater: (mate: AssemblyMateDefinition) => AssemblyMateDefinition;
}

/**
 * 删除装配体配合命令。
 */
export interface DeleteAssemblyMateCommand {
  /** 命令类型。 */
  readonly type: 'assemblyMate.delete';
  /** 要删除的 Mate ID。 */
  readonly mateId: string;
}

/**
 * 抑制装配体配合命令。
 */
export interface SuppressAssemblyMateCommand {
  /** 命令类型。 */
  readonly type: 'assemblyMate.suppress';
  /** 要抑制或解除抑制的 Mate ID。 */
  readonly mateId: string;
  /** 是否抑制。 */
  readonly suppressed: boolean;
}

/**
 * 批量锁定同轴心旋转命令。
 */
export interface LockConcentricRotationAssemblyMateCommand {
  /** 命令类型。 */
  readonly type: 'assemblyMate.lockConcentricRotation';
  /** 设计树右键触发时传入的作用域；为空时锁定整个文档中的同轴心旋转。 */
  readonly scope?: LockAssemblyConcentricRotationMatesScope;
}

/**
 * 装配体配合命令联合类型。
 */
export type AssemblyMateCommand =
  | AddAssemblyMateCommand
  | EditAssemblyMateCommand
  | DeleteAssemblyMateCommand
  | SuppressAssemblyMateCommand
  | LockConcentricRotationAssemblyMateCommand;

/**
 * 装配体配合命令执行结果。
 */
export interface AssemblyMateCommandResult {
  /** 执行后的文档。 */
  readonly document: AssemblyMateDocumentModel;
  /** 受影响的 Mate ID。 */
  readonly affectedMateIds: readonly string[];
}

/**
 * 执行装配体配合命令。
 *
 * @param document 源 Mate 文档。
 * @param command 配合命令。
 * @param now 当前时间戳。
 * @returns 命令执行结果。
 */
export function executeAssemblyMateCommand(
  document: AssemblyMateDocumentModel,
  command: AssemblyMateCommand,
  now: number = Date.now(),
): AssemblyMateCommandResult {
  if (command.type === 'assemblyMate.add') {
    return {
      document: addAssemblyMateDefinition(document, command.mateDefinition, now),
      affectedMateIds: [command.mateDefinition.mateId],
    };
  }

  if (command.type === 'assemblyMate.edit') {
    return {
      document: editAssemblyMateDefinition(document, command.mateId, command.updater, now),
      affectedMateIds: [command.mateId],
    };
  }

  if (command.type === 'assemblyMate.delete') {
    return {
      document: deleteAssemblyMateDefinition(document, command.mateId, now),
      affectedMateIds: [command.mateId],
    };
  }

  if (command.type === 'assemblyMate.lockConcentricRotation') {
    return lockAssemblyConcentricRotationMates(document, now, command.scope);
  }

  return {
    document: suppressAssemblyMateDefinition(document, command.mateId, command.suppressed, now),
    affectedMateIds: [command.mateId],
  };
}
